import {
  ACCOUNT_ATTENTION_REASONS,
  type Account,
  type AccountAttention,
  type AccountAttentionReason,
} from '@/features/accounts/accounts.api';

/**
 * Chinese copy for the 2026-09 disconnect-early-warning attention model (spec
 * `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.5 "Reason
 * copy"). Single source of truth — `FleetStatusBanner`, `ConversationAccountBanner`,
 * `useAttentionToasts` and `StatusDot` all import from here rather than
 * inlining their own strings (CLAUDE.md: "never invent a literal").
 *
 * `AccountAttention.reason` is typed as a plain `string` on the wire
 * (`accounts.api.ts`'s `accountAttentionSchema`) so a hub newer than this
 * build can add a reason without breaking the whole `/accounts` parse; every
 * function here accepts `string` and degrades an unrecognized value to
 * {@link FALLBACK_ATTENTION_LABEL} rather than throwing or rendering blank.
 */

/** Shown for a reason this build doesn't recognize (forward-compat — see the
 *  doc comment on `AccountAttention` in `accounts.api.ts`). */
export const FALLBACK_ATTENTION_LABEL = '需要处理';

/** Static reason copy — everything except `connecting_stuck`, whose label
 *  carries a computed elapsed-minutes count (see {@link attentionReasonLabel}). */
const STATIC_REASON_LABELS: Record<
  Exclude<AccountAttentionReason, 'connecting_stuck'>,
  string
> = {
  gateway_link_lost: '网关连接中断',
  restart_breaker: '网关熔断（账号未自动连接）',
  session_lost: '登录已失效，需重新扫码',
  qr_required: '等待扫码',
  // Mirrors StatusDot's existing `banned` tooltip label byte-for-byte — same
  // verdict, one place of truth for the phrase.
  banned: '疑似封号 · 重扫无效',
  down: '已停止/离线',
};

/**
 * Suffix appended to the two watchdog-owned fleet banners (restart-loop
 * breaker, 全部账号离线) — the conditions no agent can fix from the console.
 *
 * Deliberately ACTIONABLE and silent about notifications. Spec 01 §3.5 first
 * had these banners claim「已通知运维（watchdog）」, but whether anyone is paged
 * depends on alert credentials the console cannot see — both pagers stay dark
 * (log-only) until Telegram is configured. An agent who reads "ops notified"
 * waits instead of escalating; that is how an 11-day outage went unnoticed.
 * `gateway_link_lost` never carried the claim and still gets no suffix
 * (unchanged): every deploy / gateway restart shows it for a few seconds.
 */
export const ESCALATE_TO_ADMIN_SUFFIX = '请立即通知管理员';

/**
 * Leading text of `Account.status_detail` for a ban HELD by the hub's
 * fleet-wide 403-storm guard. MUST stay byte-for-byte the hub's
 * `BAN_STORM_HELD_MARKER` (`hub/app/adapter/client.py`), which writes the
 * detail as `held (403 storm): <gateway detail>`: such a number is reported
 * `banned` but was NOT stemmed — it stays `enabled:true` and keeps retrying
 * until the storm clears — so it needs an admin now, unlike an ordinary
 * (stopped) ban. Matched as an exact PREFIX (see {@link isBanStormHeld}).
 */
export const BAN_STORM_HELD_PREFIX = 'held (403 storm)';

/** The row badge / banner wording for a storm-held ban (vs. the plain
 *  「疑似封号」 of a stopped one). */
export const BAN_STORM_HELD_LABEL = '疑似封号 · 未停用（403 风暴）';

/**
 * Whether `account` is a ban HELD by the 403-storm guard: `banned` with a
 * `status_detail` that starts with {@link BAN_STORM_HELD_PREFIX} exactly
 * (case-sensitive, at position 0 — a detail merely containing it is not).
 */
export function isBanStormHeld(account: Pick<Account, 'status' | 'status_detail'>): boolean {
  const detail = account.status_detail;
  return (
    account.status === 'banned' &&
    typeof detail === 'string' &&
    detail.startsWith(BAN_STORM_HELD_PREFIX)
  );
}

/**
 * The attention label for one ACCOUNT: {@link BAN_STORM_HELD_LABEL} for a
 * storm-held ban, otherwise {@link attentionReasonLabel} of its verdict.
 */
export function accountAttentionLabel(
  account: Pick<Account, 'status' | 'status_detail'>,
  attention: Pick<AccountAttention, 'reason' | 'since'>,
  now: number,
): string {
  if (isBanStormHeld(account)) return BAN_STORM_HELD_LABEL;
  return attentionReasonLabel(attention.reason, attention.since, now);
}

/**
 * Minutes elapsed between `since` (an ISO timestamp, e.g. `AccountAttention.since`
 * / `Account.status_since`) and `now` (epoch ms). `null` when `since` is
 * missing or unparseable — the caller omits the duration text rather than
 * rendering "NaN 分钟".
 */
export function minutesSince(since: string | null | undefined, now: number): number | null {
  if (!since) return null;
  const then = Date.parse(since);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 60_000));
}

/**
 * Chinese label for one {@link AccountAttention.reason}. `connecting_stuck`
 * interpolates the elapsed minutes since `since` (spec: "连接超时 {n} 分钟");
 * every other known reason is a static string; anything outside
 * {@link ACCOUNT_ATTENTION_REASONS} — a newer hub's reason this build has
 * never seen — degrades to {@link FALLBACK_ATTENTION_LABEL}.
 */
export function attentionReasonLabel(
  reason: string,
  since: string | null | undefined,
  now: number,
): string {
  if (reason === 'connecting_stuck') {
    const mins = minutesSince(since, now);
    return mins === null ? '连接超时' : `连接超时 ${mins} 分钟`;
  }
  if ((ACCOUNT_ATTENTION_REASONS as readonly string[]).includes(reason)) {
    return STATIC_REASON_LABELS[reason as Exclude<AccountAttentionReason, 'connecting_stuck'>];
  }
  return FALLBACK_ATTENTION_LABEL;
}

/** Fallback copy keyed on `Account.status` for the rare case a hub hasn't
 *  populated `attention` at all (pre-0015 hub / optional field — "treat
 *  missing as null" per the T1-Fb brief). Intentionally NOT keyed by the full
 *  `ACCOUNT_STATUSES` union — `connected` never reaches this path (callers
 *  gate on `status !== 'connected'` first). */
const STATUS_FALLBACK_LABELS: Record<string, string> = {
  connecting: '连接中',
  qr_required: '等待扫码',
  down: '已停止/离线',
  banned: '疑似封号 · 重扫无效',
};

/**
 * Best-effort "why can't this account send" copy for a non-connected account,
 * for {@link ConversationAccountBanner} and the ThreadPanel per-member banner.
 * Prefers the hub's `attention` verdict; falls back to a plain `status`-keyed
 * label when `attention` is absent (older hub / optional field), and finally
 * to {@link FALLBACK_ATTENTION_LABEL} for a status this build has never seen
 * either.
 */
export function describeAccountUnavailable(
  account: Pick<Account, 'status' | 'attention' | 'status_detail'>,
  now: number,
): string {
  if (isBanStormHeld(account)) return BAN_STORM_HELD_LABEL;
  if (account.attention) {
    return attentionReasonLabel(account.attention.reason, account.attention.since, now);
  }
  return STATUS_FALLBACK_LABELS[account.status] ?? FALLBACK_ATTENTION_LABEL;
}

/**
 * Generic "how long has this account been in its current status" tooltip
 * fragment (spec 01 §3.5 StatusDot row: "「已 N 分钟」"). `null` when `since`
 * is missing so the caller can omit the whole `· 已 N 分钟` segment instead of
 * rendering a dangling separator.
 */
export function sinceMinutesLabel(since: string | null | undefined, now: number): string | null {
  const mins = minutesSince(since, now);
  return mins === null ? null : `已 ${mins} 分钟`;
}

/** `Account.status_detail` is capped at 300 chars server-side (migration
 *  0015); the StatusDot tooltip caps it further, per spec 01 §3.5
 *  ("status_detail (≤120 chars)"), so one gateway error string can't blow out
 *  the tooltip's width. */
const STATUS_DETAIL_TOOLTIP_MAX = 120;

/** Truncate `detail` to {@link STATUS_DETAIL_TOOLTIP_MAX} chars (ellipsis on
 *  overflow). `null`/empty pass through as `null` so the caller can skip the
 *  segment entirely. */
export function clampStatusDetail(detail: string | null | undefined): string | null {
  if (!detail) return null;
  if (detail.length <= STATUS_DETAIL_TOOLTIP_MAX) return detail;
  return `${detail.slice(0, STATUS_DETAIL_TOOLTIP_MAX - 1)}…`;
}
